/**
 * Browser-compatible AES-256-GCM decryption matching backend aldoo-crypt
 * Supports base64-encoded encrypted data with authentication
 * Compatible with Node.js aldoo-crypt module encryption
 */

const ALGORITHM = 'AES-GCM'
const NONCE_LENGTH = 12 // 96 bits for AES-GCM
const AUTH_TAG_LENGTH = 16 // 128 bits for AES-GCM
const KEY_LENGTH = 32 // 256 bits

/**
 * Convert base64 string to Uint8Array
 * @param {string} base64 - Base64 encoded string
 * @returns {Uint8Array} Decoded bytes
 */
const base64ToBytes = (base64) => {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes
}

/**
 * Convert Uint8Array to base64 string
 * @param {Uint8Array} bytes - Binary data
 * @returns {string} Base64 encoded string
 */
const bytesToBase64 = (bytes) => {
  let binaryString = ''
  for (let i = 0; i < bytes.length; i++) {
    binaryString += String.fromCharCode(bytes[i])
  }
  return btoa(binaryString)
}

/**
 * Normalize encryption key to proper format and length
 * @param {string} key - Base64 encoded key
 * @returns {Uint8Array} 32-byte key buffer
 */
const normalizeKey = (key) => {
  try {
    const keyBytes = base64ToBytes(key)
    if (keyBytes.length !== KEY_LENGTH) {
      throw new Error(`Key must be ${KEY_LENGTH} bytes when decoded, got ${keyBytes.length}`)
    }
    return keyBytes
  } catch (error) {
    throw new Error(`Invalid key format: ${error.message}`)
  }
}

/**
 * Generate a cryptographically secure 256-bit encryption key
 * @returns {string} Base64 encoded 32-byte key
 */
export const GenerateKey = () => {
  const keyBytes = crypto.getRandomValues(new Uint8Array(KEY_LENGTH))
  return bytesToBase64(keyBytes)
}

/**
 * Encrypt text using AES-256-GCM
 * @param {string} text - Plain text to encrypt
 * @param {string} key - Base64 encoded 32-byte encryption key
 * @returns {Promise<string>} Base64 encoded encrypted data
 */
export const Encrypt = async (text, key) => {
  try {
    const keyBuffer = normalizeKey(key)
    const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LENGTH))
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      ALGORITHM,
      false,
      ['encrypt']
    )
    
    const encoder = new TextEncoder()
    const plaintext = encoder.encode(text)
    
    const encrypted = await crypto.subtle.encrypt(
      { name: ALGORITHM, iv: nonce },
      cryptoKey,
      plaintext
    )
    
    // Combine nonce + encrypted data + auth tag
    const encryptedArray = new Uint8Array(encrypted)
    const combined = new Uint8Array(nonce.length + encryptedArray.length)
    combined.set(nonce, 0)
    combined.set(encryptedArray, nonce.length)
    
    return bytesToBase64(combined)
  } catch (error) {
    throw new Error(`Encryption failed: ${error.message}`)
  }
}

/**
 * Decrypt base64-encoded encrypted data using AES-256-GCM
 * @param {string} encryptedData - Base64 encoded encrypted data
 * @param {string} key - Base64 encoded 32-byte encryption key
 * @returns {Promise<string>} Decrypted plain text
 */
export const Decrypt = async (encryptedData, key) => {
  try {
    const keyBuffer = normalizeKey(key)
    const combined = base64ToBytes(encryptedData)
    
    if (combined.length < NONCE_LENGTH + AUTH_TAG_LENGTH) {
      throw new Error('Invalid encrypted data format')
    }
    
    // Extract nonce and encrypted data
    const nonce = combined.slice(0, NONCE_LENGTH)
    const encrypted = combined.slice(NONCE_LENGTH)
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      ALGORITHM,
      false,
      ['decrypt']
    )
    
    const decrypted = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv: nonce },
      cryptoKey,
      encrypted
    )
    
    const decoder = new TextDecoder()
    return decoder.decode(decrypted)
  } catch (error) {
    if (error.name === 'OperationError') {
      throw new Error('Failed to authenticate encrypted data - possible tampering detected')
    }
    throw new Error(`Decryption failed: ${error.message}`)
  }
}

export default Decrypt
